import { Location } from '@angular/common';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isEmbeddedInPlatformShell } from '@core/utils/platform-embed';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationExtras,
  NavigationStart,
  Router,
} from '@angular/router';

/**
 * Whether the app runs embedded in the platform shell (hub-ops), where the
 * host owns the browser URL/history. Mirrors the detection in `main.ts`.
 */
export const NAVIGATION_BACK_IS_EMBEDDED = new InjectionToken<() => boolean>(
  'NAVIGATION_BACK_IS_EMBEDDED',
  {
    factory: () => isEmbeddedInPlatformShell,
  },
);

/**
 * Router-aware replacement for `history.back()` on in-app cancel/close
 * buttons.
 *
 * Raw `history.back()` breaks when the Designer is embedded in the platform
 * shell (hub-ops): the host owns the browser URL (`bindRouterToHost` syncs it
 * via `platform:navigated` events), so browser-history back does not step the
 * embedded router back (SH-15180).
 *
 * The service records its own in-app URL stack from router events. `back()`
 * uses real browser history in standalone mode (faithful pop semantics) and a
 * router navigation to the recorded previous URL when embedded; when there is
 * no in-app previous page (direct load / deep link), it navigates to the given
 * fallback instead of doing nothing.
 *
 * It is instantiated at bootstrap (see `CoreModule`) so the stack covers every
 * navigation from app start.
 */
@Injectable({ providedIn: 'root' })
export class NavigationBackService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly isEmbedded = inject(NAVIGATION_BACK_IS_EMBEDDED);

  /** In-app navigation history; the last entry is the current URL. */
  private readonly urlStack: string[] = [];
  private navigatingBack = false;
  private lastNavigationTrigger?: string;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.lastNavigationTrigger = event.navigationTrigger;
      } else if (event instanceof NavigationEnd) {
        this.record(event.urlAfterRedirects);
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.navigatingBack = false;
      }
    });
  }

  /**
   * Navigate to the previous in-app URL, or to `fallbackCommands` when there
   * is none (e.g. the page was loaded directly).
   */
  back(fallbackCommands: readonly unknown[], extras?: NavigationExtras): void {
    const previousUrl = this.urlStack.length > 1 ? this.urlStack[this.urlStack.length - 2] : null;
    if (!previousUrl) {
      void this.router.navigate([...fallbackCommands], extras);
      return;
    }
    this.navigatingBack = true;
    if (this.isEmbedded()) {
      void this.router.navigateByUrl(previousUrl);
    } else {
      this.location.back();
    }
  }

  private record(url: string): void {
    const isBack = this.navigatingBack || this.lastNavigationTrigger === 'popstate';
    this.navigatingBack = false;
    const backTarget = this.urlStack.length > 1 ? this.urlStack[this.urlStack.length - 2] : null;
    if (isBack && backTarget === url) {
      this.urlStack.pop();
    } else if (this.urlStack[this.urlStack.length - 1] !== url) {
      this.urlStack.push(url);
    }
  }
}
